import { useState, useRef } from "react";
import { supabase } from "./supabase";

// ============================================================
// PARSER: Match Trader (CSV, coma como separador)
// ============================================================
function parseMatchTraderCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) throw new Error('CSV vacío o inválido');

  const headers = lines[0].split(',').map(h => h.trim());

  const idx = {
    id:         headers.indexOf('ID'),
    symbol:     headers.indexOf('Símbolo'),
    openTime:   headers.indexOf('Hora de apertura'),
    volume:     headers.indexOf('Volumen'),
    type:       headers.indexOf('Operación'),
    closeTime:  headers.indexOf('Hora de cierre'),
    openPrice:  headers.indexOf('Precio de apertura'),
    closePrice: headers.indexOf('Precio de cierre'),
    profit:     headers.indexOf('Beneficio'),
    reason:     headers.indexOf('Razón'),
    commission: headers.indexOf('Comisión'),
  };

  const required = ['id', 'symbol', 'openTime', 'volume', 'type', 'closeTime', 'openPrice', 'closePrice', 'profit'];
  for (const key of required) {
    if (idx[key] === -1) throw new Error(`Columna no encontrada: ${key}. ¿Es un CSV de Match Trader?`);
  }

  const tradesMap = {};

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith('Total')) continue;

    const cols = line.split(',');
    const rawId = cols[idx.id]?.trim();
    if (!rawId) continue;

    const profit = parseFloat(cols[idx.profit]) || 0;
    const commission = parseFloat(cols[idx.commission]) || 0;
    const reason = cols[idx.reason]?.trim() || '';

    if (reason === 'Cierre parcial') {
      if (tradesMap[rawId]) {
        tradesMap[rawId].pnl += profit + commission;
      }
      continue;
    }

    const openPrice = parseFloat(cols[idx.openPrice]) || 0;
    const closePrice = parseFloat(cols[idx.closePrice]) || 0;

    tradesMap[rawId] = {
      pair:        cols[idx.symbol]?.trim() || '',
      type:        cols[idx.type]?.trim() || '',
      lots:        parseFloat(cols[idx.volume]) || 0,
      open_price:  openPrice,
      close_price: closePrice,
      pnl:         profit + commission,
      status:      profit >= 0 ? 'WIN' : 'LOSS',
      setup:       'Manual',
      session:     getSession(cols[idx.openTime]?.trim()),
      emotion:     'calm',
      note:        `Importado desde Match Trader · ${reason}`,
      images:      [],
      rr:          null,
      created_at:  parseDateMatchTrader(cols[idx.openTime]?.trim()),
    };
  }

  return Object.values(tradesMap);
}

// ============================================================
// PARSER: TradeLocker (TSV/CSV, separador tab o punto y coma)
// ============================================================
function parseTradeLockerCSV(text) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) throw new Error('CSV vacío o inválido');

  // Detectar separador: tab, punto y coma, o coma
  const firstLine = lines[0];
  let sep = '\t';
  if (firstLine.includes('\t')) sep = '\t';
  else if (firstLine.includes(';')) sep = ';';
  else sep = ',';

  const headers = lines[0].split(sep).map(h => h.trim().replace(/^"/, '').replace(/"$/, ''));

  // Buscar columnas (soporta español e inglés)
  const find = (...names) => {
    for (const name of names) {
      const i = headers.findIndex(h => h.toLowerCase().includes(name.toLowerCase()));
      if (i !== -1) return i;
    }
    return -1;
  };

  const idx = {
    symbol:     find('Instrumento', 'Instrument', 'Symbol'),
    openTime:   find('Hora de Entrada', 'Open Time', 'Entry Time'),
    side:       find('Lado', 'Side', 'Direction'),
    lots:       find('Cantidad', 'Volume', 'Lots', 'Quantity'),
    openPrice:  find('Precio de Entrada', 'Entry Price', 'Open Price'),
    closeTime:  find('Hora de Salida', 'Close Time', 'Exit Time'),
    closePrice: find('Precio de Salida', 'Exit Price', 'Close Price'),
    commission: find('Comisión', 'Commission'),
    swaps:      find('Swaps', 'Swap'),
    pnl:        find('P&L Neto', 'Net P&L', 'Net Profit'),
    pnlGross:   find('P&L', 'Gross P&L', 'Profit'),
    orderId:    find('ID de Orden', 'Order ID'),
    posId:      find('ID de Posición', 'Position ID'),
  };

  // Verificar mínimos
  if (idx.symbol === -1) throw new Error('Columna "Instrumento" no encontrada. ¿Es un CSV de TradeLocker?');
  if (idx.openTime === -1) throw new Error('Columna "Hora de Entrada" no encontrada.');
  if (idx.side === -1) throw new Error('Columna "Lado" no encontrada.');

  const trades = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const cols = line.split(sep).map(c => c.trim().replace(/^"/, '').replace(/"$/, ''));

    const symbol = cols[idx.symbol] || '';
    if (!symbol) continue;

    // Parsear lado (Comprar/Vender o Buy/Sell)
    const rawSide = (cols[idx.side] || '').toLowerCase();
    let type = 'BUY';
    if (rawSide.includes('vender') || rawSide.includes('sell') || rawSide.includes('short')) type = 'SELL';
    if (rawSide.includes('comprar') || rawSide.includes('buy') || rawSide.includes('long')) type = 'BUY';

    // Parsear números (formato europeo: "1.177,950" o "1,177950" o "-0,11 US$")
    const parseNum = (val) => {
      if (!val) return 0;
      let clean = val.replace(/[A-Za-z$€£¥\s]/g, '').trim(); // quitar moneda y espacios
      if (!clean || clean === '-' || clean === '–') return 0;
      // Si tiene punto Y coma → formato europeo "1.234,56" → quitar puntos, coma→punto
      if (clean.includes('.') && clean.includes(',')) {
        clean = clean.replace(/\./g, '').replace(',', '.');
      }
      // Si solo tiene coma → puede ser decimal europeo "0,01" o separador miles "1,234"
      else if (clean.includes(',')) {
        // Si hay exactamente 3 dígitos después de la coma → separador de miles
        const parts = clean.split(',');
        if (parts[1] && parts[1].length === 3 && !parts[1].includes('.')) {
          clean = clean.replace(',', ''); // miles
        } else {
          clean = clean.replace(',', '.'); // decimal
        }
      }
      const num = parseFloat(clean);
      return isNaN(num) ? 0 : num;
    };

    const lots = parseNum(cols[idx.lots]);
    const openPrice = parseNum(cols[idx.openPrice]);
    const closePrice = idx.closePrice !== -1 ? parseNum(cols[idx.closePrice]) : 0;
    const commission = idx.commission !== -1 ? parseNum(cols[idx.commission]) : 0;
    const swaps = idx.swaps !== -1 ? parseNum(cols[idx.swaps]) : 0;

    // Usar P&L Neto si existe, sino P&L bruto + comisión + swaps
    let pnl = 0;
    if (idx.pnl !== -1 && cols[idx.pnl]) {
      pnl = parseNum(cols[idx.pnl]);
    } else if (idx.pnlGross !== -1) {
      pnl = parseNum(cols[idx.pnlGross]) + commission + swaps;
    }

    const openTimeStr = cols[idx.openTime] || '';
    const closeTimeStr = idx.closeTime !== -1 ? (cols[idx.closeTime] || '') : '';

    // Solo importar trades cerrados (que tengan hora de salida)
    if (!closeTimeStr) continue;

    trades.push({
      pair:        symbol.replace(/[^A-Za-z0-9/.]/g, ''),
      type:        type,
      lots:        lots,
      open_price:  openPrice,
      close_price: closePrice,
      pnl:         pnl,
      status:      pnl >= 0 ? 'WIN' : 'LOSS',
      setup:       'Manual',
      session:     getSessionFromTradeLocker(openTimeStr),
      emotion:     'calm',
      note:        `Importado desde TradeLocker`,
      images:      [],
      rr:          null,
      created_at:  parseDateTradeLocker(openTimeStr),
    });
  }

  return trades;
}

// ============================================================
// Utilidades de fecha
// ============================================================

// Match Trader: "DD/MM/YYYY HH:MM:SS"
function parseDateMatchTrader(str) {
  if (!str) return new Date().toISOString();
  const [datePart, timePart] = str.split(' ');
  if (!datePart) return new Date().toISOString();
  const [day, month, year] = datePart.split('/');
  return new Date(`${year}-${month}-${day}T${timePart || '00:00:00'}Z`).toISOString();
}

// TradeLocker: "DD/MM/YYYY, HH:MM:SS" o "DD/MM/YYYY HH:MM:SS" o "YYYY-MM-DD HH:MM:SS"
function parseDateTradeLocker(str) {
  if (!str) return new Date().toISOString();
  let clean = str.replace(',', '').trim(); // quitar la coma entre fecha y hora
  // Formato DD/MM/YYYY
  if (clean.match(/^\d{2}\/\d{2}\/\d{4}/)) {
    const [datePart, timePart] = clean.split(/\s+/);
    const [day, month, year] = datePart.split('/');
    return new Date(`${year}-${month}-${day}T${timePart || '00:00:00'}Z`).toISOString();
  }
  // Formato YYYY-MM-DD (ya ISO)
  if (clean.match(/^\d{4}-\d{2}-\d{2}/)) {
    return new Date(clean.replace(' ', 'T') + 'Z').toISOString();
  }
  return new Date().toISOString();
}

function getSession(dateStr) {
  if (!dateStr) return 'London';
  const [, timePart] = dateStr.split(' ');
  if (!timePart) return 'London';
  const hour = parseInt(timePart.split(':')[0]);
  if (hour >= 0 && hour < 8) return 'Asia';
  if (hour >= 8 && hour < 12) return 'London';
  if (hour >= 12 && hour < 17) return 'New York';
  return 'London';
}

function getSessionFromTradeLocker(dateStr) {
  if (!dateStr) return 'London';
  const clean = dateStr.replace(',', '').trim();
  const parts = clean.split(/\s+/);
  const timePart = parts[1] || '';
  if (!timePart) return 'London';
  const hour = parseInt(timePart.split(':')[0]);
  if (hour >= 0 && hour < 8) return 'Asia';
  if (hour >= 8 && hour < 12) return 'London';
  if (hour >= 12 && hour < 17) return 'New York';
  return 'London';
}

// ============================================================
// Componente principal
// ============================================================
export default function ImportHistory({ session, accounts, setTrades }) {
  const [platform, setPlatform] = useState('matchtrader');
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [success, setSuccess] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const fileRef = useRef();

  const platforms = [
    { id: 'matchtrader', label: 'Match Trader', status: 'ready' },
    { id: 'tradelocker', label: 'TradeLocker',  status: 'ready' },
    { id: 'ctrader',     label: 'cTrader',       status: 'soon' },
    { id: 'dxtrade',     label: 'DXtrade',       status: 'soon' },
    { id: 'mt4',         label: 'MT4 / MT5',     status: 'soon' },
  ];

  const instructions = {
    matchtrader: [
      'Abre Match Trader → menú → "Exportar"',
      'Marca "Posiciones cerradas" y formato "CSV"',
      'Selecciona el rango de fechas y dale a "Exportar"',
      'Sube el fichero descargado aquí',
    ],
    tradelocker: [
      'Abre TradeLocker → pestaña "Posiciones Cerradas"',
      'Selecciona el rango de fechas en "Resumen"',
      'Haz click en el icono de compartir (arriba a la derecha)',
      'Selecciona "Export account History" → descarga el CSV',
      'Sube el fichero descargado aquí',
    ],
  };

  const handleFile = (file) => {
    setError('');
    setPreview(null);
    setSuccess('');
    if (!file || !file.name.endsWith('.csv')) {
      setError('El fichero debe ser un CSV.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let trades;
        if (platform === 'tradelocker') {
          trades = parseTradeLockerCSV(e.target.result);
        } else {
          trades = parseMatchTraderCSV(e.target.result);
        }
        if (trades.length === 0) {
          setError('No se encontraron trades cerrados en el fichero.');
          return;
        }
        setPreview(trades);
      } catch (err) {
        setError(err.message);
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const handleImport = async () => {
    if (!preview || preview.length === 0) return;
    if (!selectedAccount) {
      setError('Selecciona una cuenta antes de importar.');
      return;
    }
    setImporting(true);
    setError('');

    try {
      const toInsert = preview.map(t => ({
        ...t,
        user_id:    session.user.id,
        account_id: parseInt(selectedAccount),
      }));

      const { data, error: dbErr } = await supabase
        .from('trades')
        .insert(toInsert)
        .select();

      if (dbErr) throw new Error(dbErr.message);

      setSuccess(`✅ ${data.length} trades importados correctamente.`);
      setPreview(null);
      if (setTrades) {
        const { data: allTrades } = await supabase
          .from('trades')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });
        if (allTrades) setTrades(allTrades);
      }
    } catch (err) {
      setError('Error al importar: ' + err.message);
    }
    setImporting(false);
  };

  const totalPnl = preview ? preview.reduce((s, t) => s + t.pnl, 0) : 0;
  const wins = preview ? preview.filter(t => t.pnl > 0).length : 0;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 20 }}>📥</span>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--t1)' }}>Importar historial</div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>
          Conecta cualquier plataforma aunque no uses el EA
        </div>
      </div>

      {/* Selector de plataforma */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        {platforms.map(p => (
          <div
            key={p.id}
            onClick={() => p.status === 'ready' && setPlatform(p.id)}
            style={{
              background: 'var(--s1)',
              border: `${platform === p.id ? '2px' : '1px'} solid ${platform === p.id ? 'var(--ac)' : 'var(--b1)'}`,
              borderRadius: 10,
              padding: '12px 14px',
              cursor: p.status === 'ready' ? 'pointer' : 'not-allowed',
              opacity: p.status === 'ready' ? 1 : 0.5,
              position: 'relative',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', marginBottom: 2 }}>{p.label}</div>
            <div style={{ fontSize: 10, color: p.status === 'ready' ? 'var(--green)' : 'var(--t4)' }}>
              {p.status === 'ready' ? '● Disponible' : '○ Próximamente'}
            </div>
            {platform === p.id && (
              <div style={{
                position: 'absolute', top: 8, right: 8,
                width: 8, height: 8, borderRadius: '50%', background: 'var(--ac)'
              }} />
            )}
          </div>
        ))}
      </div>

      {/* Instrucciones */}
      <div style={{ background: 'var(--s1)', border: '1px solid var(--b1)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
          Cómo exportar desde {platforms.find(p => p.id === platform)?.label}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {(instructions[platform] || []).map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: 'var(--ac-glow)', color: 'var(--ac)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, flexShrink: 0,
                border: '1px solid var(--ac)'
              }}>{i + 1}</div>
              <div style={{ fontSize: 13, color: 'var(--t2)' }}>{step}</div>
            </div>
          ))}
        </div>

        {/* Selector de cuenta */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            Cuenta de destino
          </div>
          <select
            value={selectedAccount}
            onChange={e => setSelectedAccount(e.target.value)}
            style={{
              background: 'var(--s2)', border: '1px solid var(--b1)',
              borderRadius: 8, color: 'var(--t1)', fontSize: 13,
              padding: '8px 12px', width: '100%', outline: 'none',
              fontFamily: 'var(--sans)'
            }}
          >
            <option value=''>Selecciona una cuenta...</option>
            {accounts && accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name || `Cuenta ${a.id}`}</option>
            ))}
          </select>
        </div>

        {/* Zona de drop */}
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? 'var(--ac)' : 'var(--b2)'}`,
            borderRadius: 10,
            padding: '32px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? 'var(--ac-glow)' : 'var(--s2)',
            transition: 'all .15s',
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)', marginBottom: 4 }}>
            Arrastra tu CSV aquí
          </div>
          <div style={{ fontSize: 12, color: 'var(--t3)' }}>o haz click para seleccionar</div>
          <input
            ref={fileRef}
            type='file'
            accept='.csv'
            style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])}
          />
        </div>

        {error && (
          <div style={{
            marginTop: 12, background: 'var(--red-bg)', border: '1px solid rgba(220,38,38,0.2)',
            borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', fontFamily: 'var(--mono)'
          }}>{error}</div>
        )}

        {success && (
          <div style={{
            marginTop: 12, background: 'var(--green-bg)', border: '1px solid rgba(5,150,105,0.2)',
            borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--green)', fontFamily: 'var(--mono)'
          }}>{success}</div>
        )}
      </div>

      {/* Preview de trades */}
      {preview && (
        <div style={{ background: 'var(--s1)', border: '1px solid var(--b1)', borderRadius: 12, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)', marginBottom: 2 }}>
                Vista previa — {preview.length} trades detectados
              </div>
              <div style={{ fontSize: 11, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>
                {wins} ganadores · PnL total: <span style={{ color: totalPnl >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
                  {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}
                </span>
              </div>
            </div>
            <button
              onClick={handleImport}
              disabled={importing}
              style={{
                background: importing ? 'var(--s3)' : 'var(--ac)',
                color: importing ? 'var(--t3)' : '#fff',
                border: 'none', borderRadius: 9,
                padding: '10px 20px', fontSize: 13, fontWeight: 700,
                cursor: importing ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--sans)',
              }}
            >
              {importing ? 'Importando...' : `Importar ${preview.length} trades →`}
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--b1)' }}>
                  {['Par', 'Tipo', 'Lots', 'Entrada', 'Salida', 'Sesión', 'PnL'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--t3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.slice(0, 10).map((t, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--b1)' }}>
                    <td style={{ padding: '7px 8px', fontWeight: 600, color: 'var(--t1)' }}>{t.pair}</td>
                    <td style={{ padding: '7px 8px' }}>
                      <span style={{
                        background: t.type === 'BUY' ? 'var(--green-bg)' : 'var(--red-bg)',
                        color: t.type === 'BUY' ? 'var(--green)' : 'var(--red)',
                        borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600
                      }}>{t.type}</span>
                    </td>
                    <td style={{ padding: '7px 8px', color: 'var(--t3)' }}>{t.lots}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--t3)', fontFamily: 'var(--mono)' }}>{t.open_price}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--t3)', fontFamily: 'var(--mono)' }}>{t.close_price}</td>
                    <td style={{ padding: '7px 8px', color: 'var(--t3)' }}>{t.session}</td>
                    <td style={{ padding: '7px 8px', fontWeight: 700, color: t.pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                      {t.pnl >= 0 ? '+' : ''}${t.pnl.toFixed(2)}
                    </td>
                  </tr>
                ))}
                {preview.length > 10 && (
                  <tr>
                    <td colSpan={7} style={{ padding: '8px', textAlign: 'center', fontSize: 11, color: 'var(--t4)', fontFamily: 'var(--mono)' }}>
                      ... y {preview.length - 10} trades más
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
